import { useCallback, useEffect, useRef, useState } from 'react';
import api from 'common/api';
import type { BundleFile } from '../utils/bundleFileSearch';

interface BundleSelection {
  id: string;
  [key: string]: any;
}

interface UseBundleFilesCacheParams {
  caseId: string | null;
  selectedBundles: BundleSelection[];
}

interface UseBundleFilesCacheResult {
  bundleFiles: BundleFile[];
  isFetching: boolean;
  error: string | null;
}

export function useBundleFilesCache({
  caseId,
  selectedBundles,
}: UseBundleFilesCacheParams): UseBundleFilesCacheResult {
  const cacheRef = useRef<Map<string, BundleFile[]>>(new Map());
  const versionRef = useRef(0);
  const [bundleFiles, setBundleFiles] = useState<BundleFile[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedBundleIds = selectedBundles.map((b) => String(b.id));

  const fetchBundles = useCallback(async () => {
    if (!caseId || selectedBundleIds.length === 0) {
      setBundleFiles([]);
      setError(null);
      return;
    }

    const currentVersion = ++versionRef.current;
    const cache = cacheRef.current;

    // Remove stale entries
    for (const cachedId of cache.keys()) {
      if (!selectedBundleIds.includes(cachedId)) {
        cache.delete(cachedId);
      }
    }

    // Identify bundles that need fetching
    const newIds = selectedBundleIds.filter((id) => !cache.has(id));

    if (newIds.length === 0) {
      // All cached — just flatten and set
      const allFiles: BundleFile[] = [];
      for (const id of selectedBundleIds) {
        const files = cache.get(id);
        if (files) allFiles.push(...files);
      }
      setBundleFiles(allFiles);
      setError(null);
      return;
    }

    setIsFetching(true);
    setError(null);

    try {
      const results = await Promise.all(
        newIds.map(async (bundleId) => {
          const res: any = await api.get(`/cases/${caseId}/folders/${bundleId}/files`);
          if (res && res.error) {
            return { bundleId, files: [] as BundleFile[], error: res.error };
          }
          const files: BundleFile[] = Array.isArray(res) ? res : [];
          return { bundleId, files, error: null };
        }),
      );

      // Discard if stale
      if (versionRef.current !== currentVersion) return;

      let hasError = false;
      for (const result of results) {
        if (result.error) {
          hasError = true;
        } else {
          cache.set(result.bundleId, result.files);
        }
      }

      // Flatten all cached files for selected bundles
      const allFiles: BundleFile[] = [];
      for (const id of selectedBundleIds) {
        const files = cache.get(id);
        if (files) allFiles.push(...files);
      }
      setBundleFiles(allFiles);
      if (hasError) {
        setError('Some bundle files could not be loaded');
      }
    } catch (e) {
      if (versionRef.current !== currentVersion) return;
      setError('Failed to load bundle files');
      console.error('useBundleFilesCache error:', e);
    } finally {
      if (versionRef.current === currentVersion) {
        setIsFetching(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, selectedBundleIds.join(',')]);

  useEffect(() => {
    fetchBundles();
  }, [fetchBundles]);

  return { bundleFiles, isFetching, error };
}
